import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { usePersistentDismissible } from '../../misc/use-persistent-dismissable'
import { ContextShell } from '../../../modules/context'
import { useManageSubscription } from '../../user/use-manage-subscription'
import { useExperiment } from '../../experiment'

export interface UseReactivateSubscriptionPaywallProps {
  id: string
}

export interface UseReactivateSubscriptionPaywall {
  loading: boolean
  show: boolean
  onClickPaywall: () => Promise<void>
  onClosePaywall: (e: React.MouseEvent<HTMLButtonElement>) => void
  userInExperiment: boolean
}

export const REACTIVATE_SUBSCRIPTION_KEY =
  'moises:reactivate-subscription-start'

export const useReactivateSubscriptionPaywall = ({
  id
}: UseReactivateSubscriptionPaywallProps): UseReactivateSubscriptionPaywall => {
  const didFetchRef = useRef(false)
  const { user } = useContext(ContextShell)
  const { loading, openBillingPortal } = useManageSubscription()
  const { getExperimentVariant } = useExperiment()
  const [userInExperiment, setUserInExperiment] = useState<boolean>(false)

  useEffect(() => {
    if (didFetchRef.current) return

    const fetchUserInExperiment = async (): Promise<void> => {
      didFetchRef.current = true

      const variant = await getExperimentVariant({
        flag: 'churnPrevention',
        forceAssign: false
      })

      setUserInExperiment(variant === 'variant_a')
    }

    fetchUserInExperiment()
  }, [userInExperiment, getExperimentVariant])

  const enableReactivateSubscription = useMemo(() => {
    return !!(user?.subscription?.isExpiringSoon && userInExperiment)
  }, [userInExperiment, user?.subscription?.isExpiringSoon])

  const { show, onDismiss } = usePersistentDismissible({
    id: `reactivate-subscription-${id}`,
    type: 'local',
    enabled: true
  })

  const onClosePaywall = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation()
      onDismiss()
    },
    [onDismiss]
  )

  const onClickPaywall = useCallback(async () => {
    localStorage.setItem(
      REACTIVATE_SUBSCRIPTION_KEY,
      JSON.stringify({
        userId: user?.id,
        autoRenew: Boolean(user?.subscription?.details?.autoRenew),
        startedAt: new Date().toISOString()
      })
    )

    await openBillingPortal()
  }, [openBillingPortal, user?.id, user?.subscription?.details?.autoRenew])

  return {
    loading,
    show: show && enableReactivateSubscription,
    userInExperiment,
    onClosePaywall,
    onClickPaywall
  }
}
